import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchInternalQuestionIdApi,
  getLatestRecommendedQuestionApi,
} from '../Pal/api/learners';
import { Question } from '@protos/content_management/content.db_pb';
import {
  ContentAttempt,
  QAttemptResultEnum,
  StudentAssessmentResponse,
} from '@protos/learning_management/lms.db_pb';
import {
  getModelCorrectAnswer,
  IconWrapper,
  modelCorrectToAnswerArray,
  QuestionContainerWithSolution,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';

const DemoPalQuestion = () => {
  const [geneoQuestion, setGeneoQuestion] = useState<Question | undefined>(
    undefined
  );
  const [enablePolling, setEnablePolling] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const userAttemptedAnswer = useMemo(() => {
    if (!geneoQuestion) {
      return undefined;
    }
    const modelCorrect = getModelCorrectAnswer(geneoQuestion);
    const answer = modelCorrectToAnswerArray(modelCorrect);
    return answer.length ? answer : undefined;
  }, [geneoQuestion]);

  const currentResponse = useMemo(() => {
    if (!geneoQuestion?.questionId || !userAttemptedAnswer) {
      return undefined;
    }
    return new StudentAssessmentResponse({
      questionId: geneoQuestion.questionId,
      responses: [
        new ContentAttempt({
          answer: userAttemptedAnswer,
        }),
      ],
      isCorrect: QAttemptResultEnum.RESPONSE_CORRECT,
    });
  }, [geneoQuestion?.questionId, userAttemptedAnswer]);

  const { ContentCommonAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const onSubmit = useCallback(async () => {
    try {
      const recommendation = await getLatestRecommendedQuestionApi();
      const questionIdReceived = recommendation.question_id;
      if (!questionIdReceived) {
        setGeneoQuestion(undefined);
        return;
      }
      const geneo_question_id = (
        await fetchInternalQuestionIdApi(questionIdReceived)
      )?.question_id;
      if (geneo_question_id) {
        const res =
          await ContentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchQuestions(
            {
              questionIds: [geneo_question_id],
            }
          );
        if (res.data) {
          setGeneoQuestion(res.data.questions[0]);
        } else {
          setGeneoQuestion(undefined);
        }
      } else {
        setGeneoQuestion(undefined);
      }
    } catch (err) {
      console.error(err);
    }
  }, [ContentCommonAPIServiceV1ClientWithStatusCodeHandler]);

  useEffect(() => {
    if (!enablePolling) {
      return;
    }
    void onSubmit();
    const intervalId = setInterval(() => {
      void onSubmit();
    }, 3000);
    return () => clearInterval(intervalId);
  }, [enablePolling, onSubmit]);

  return (
    <Box
      sx={{
        margin: '15px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <Typography variant="h6">
        Click on the Submit button to get the latest PAL question in the current
        session.
      </Typography>
      <div>
        <Button variant="contained" color="primary" onClick={() => onSubmit()}>
          Submit
        </Button>
      </div>
      <FormControlLabel
        control={
          <Checkbox
            checked={enablePolling}
            onChange={(e) => setEnablePolling(e.target.checked)}
          />
        }
        label="Enable polling"
      />
      <Typography variant="body2" color="text.secondary">
        Enabling polling will fetch the latest questions automatically every 3
        seconds.
      </Typography>
      {geneoQuestion && (
        <div>
          <QuestionContainerWithSolution
            question={geneoQuestion}
            showAnswer={true}
            homeworkAttempt={true}
            showSubmitButton={false}
            userAttemptedAnswer={userAttemptedAnswer}
            currentResponse={currentResponse}
            headerRightElement={
              geneoQuestion?.isAiGenerated ? (
                <Box
                  sx={{
                    color: '#007CDC',
                    border: '1px solid #0AA34F57',
                    borderRadius: '16px',
                    p: '4px 8px',
                    cursor: 'pointer',
                    background: '#D4FFE7',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginRight: 'auto',
                    marginLeft: '10px',
                  }}
                >
                  <Typography color="#1D1D1D" variant="smallText">
                    {isMobile ? 'AI' : 'AI Generated'}
                  </Typography>
                </Box>
              ) : undefined
            }
          />
        </div>
      )}
    </Box>
  );
};

export default DemoPalQuestion;
